import { Building2, Mail, Phone, Plus, Search, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useParties, useTransactions } from './api'
import { Money, Section } from './components/FinanceBits'
import { PartyForm } from './components/PartyForm'
import { SensitiveIban } from './components/SensitiveIban'
import { FINANCE_PARTY_WRITE } from './permissions'
import type { Counterparty } from './types'

export function PartiesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const parties = useParties()
  const txs = useTransactions()
  const [kind, setKind] = useState<'all' | 'supplier' | 'customer'>('all')
  const [q, setQ] = useState('')
  const [form, setForm] = useState<{ open: boolean; party: Counterparty | null }>({ open: false, party: null })
  const canWrite = can(FINANCE_PARTY_WRITE)
  const term = q.trim().toLocaleLowerCase('tr')
  const rows = (parties.data ?? []).filter((p) => (kind === 'all' || p.kind === kind) && (!term || `${p.name} ${p.taxNo}`.toLocaleLowerCase('tr').includes(term)))
  const openOf = (id: string) => (txs.data ?? []).filter((x) => x.counterpartyId === id && x.status !== 'paid')

  const { pageRows, pager } = usePagination(rows, [kind, term])
  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.parties.title')}
        description={t('finance.parties.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setForm({ open: true, party: null })}>
              <Plus />
              {t('finance.parties.add')}
            </Button>
          ) : null
        }
      />
      <Section
        title={t('finance.parties.list')}
        icon={Users}
        bodyClassName="p-0"
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <Tabs value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <TabsList size="sm">
                {(['all', 'supplier', 'customer'] as const).map((k) => (
                  <TabsTrigger key={k} value={k}>
                    {t(`finance.parties.filter.${k}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="relative sm:w-56">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('finance.parties.search')} aria-label={t('finance.parties.search')} className="h-8 pl-8" />
            </div>
          </div>
        }
      >
        {parties.isError ? (
          <ErrorState error={parties.error} onRetry={() => void parties.refetch()} />
        ) : parties.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Users} title={t('finance.parties.empty')} />
        ) : (
          <>
            <ul className="grid grid-cols-1 divide-y lg:grid-cols-2 lg:divide-y-0 lg:[&>li:nth-child(n+3)]:border-t lg:[&>li:nth-child(odd)]:border-r">
              {pageRows.map((p) => {
                const open = openOf(p.id)
                return (
                  <li key={p.id} className="space-y-2 p-4">
                    <div className="flex items-start gap-3">
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Building2 className="size-4" aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{p.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {t(`finance.parties.kind.${p.kind}`)}
                          {p.taxNo ? ` · ${t('finance.taxNo', { no: p.taxNo })}` : ''}
                          {p.taxOffice ? ` · ${p.taxOffice}` : ''}
                        </p>
                      </div>
                      {canWrite ? (
                        <Button size="sm" variant="ghost" onClick={() => setForm({ open: true, party: p })}>
                          {t('common.edit')}
                        </Button>
                      ) : null}
                    </div>
                    <dl className="grid grid-cols-1 gap-1 pl-12 text-xs sm:grid-cols-[6rem_1fr]">
                      <dt className="text-muted-foreground">{t('finance.parties.bank')}</dt>
                      <dd className="min-w-0">
                        {p.bankName || '—'}
                        {p.ibanMasked ? (
                          <span className="mt-0.5 block">
                            <SensitiveIban target="party" id={p.id} masked={p.ibanMasked} />
                          </span>
                        ) : null}
                      </dd>
                      {p.email || p.phone ? (
                        <>
                          <dt className="text-muted-foreground">{t('finance.parties.contact')}</dt>
                          <dd className="flex flex-wrap gap-x-3">
                            {p.email ? (
                              <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                                <Mail className="size-3" aria-hidden />
                                {p.email}
                              </a>
                            ) : null}
                            {p.phone ? (
                              <span className="inline-flex items-center gap-1">
                                <Phone className="size-3" aria-hidden />
                                {p.phone}
                              </span>
                            ) : null}
                          </dd>
                        </>
                      ) : null}
                      <dt className="text-muted-foreground">{t('finance.parties.open')}</dt>
                      <dd>{open.length ? t('finance.parties.openValue', { count: open.length }) : t('finance.parties.noOpen')}</dd>
                    </dl>
                    {open.length ? (
                      <p className="pl-12 text-xs text-muted-foreground">
                        {t('finance.parties.lastOpen')}: <Money value={open[0]?.remaining ?? '0'} className="font-medium text-foreground" />
                      </p>
                    ) : null}
                  </li>
                )
              })}
            </ul>
            <Pager {...pager} />
          </>
        )}
      </Section>
      {form.open ? <PartyForm key={form.party?.id ?? 'new'} party={form.party} onDone={() => setForm({ open: false, party: null })} /> : null}
    </>
  )
}
